// In-memory ring buffer + pub/sub for the console panel.
//
// Two ingress channels feed the buffer:
//   1. installConsoleCapture(): mirrors console.warn / console.error into the
//      buffer while still calling the originals (DevTools is unchanged).
//   2. installIntentTaps(store): subscribes to a curated set of intents and
//      records human-readable lines (dataset switch, theme cycle, etc.).
// Other code can also call log() directly.
//
// Persistence is session-scoped: the buffer lives in module state. Closing
// the console panel does not clear it — only a page reload does.

import { INTENTS } from '@store/intents';
import type { Store } from '@store/store';
import type { State } from '@store/schema';

export type LogLevel = 'info' | 'warn' | 'error' | 'debug' | 'cmd';
export interface LogEntry { t: number; level: LogLevel; msg: string; }

const CAP = 500;
const buffer: LogEntry[] = [];
const subscribers = new Set<(e: LogEntry) => void>();

export function log(level: LogLevel, msg: string): void {
    const entry: LogEntry = { t: Date.now(), level, msg };
    buffer.push(entry);
    if (buffer.length > CAP) buffer.splice(0, buffer.length - CAP);
    subscribers.forEach((fn) => { try { fn(entry); } catch { /* swallow */ } });
}

export function getBuffer(): readonly LogEntry[] { return buffer; }

export function clearBuffer(): void {
    buffer.length = 0;
    // Surface a sentinel so renderers can repaint from empty without a
    // dedicated "buffer-cleared" channel.
    const entry: LogEntry = { t: Date.now(), level: 'debug', msg: '[cleared]' };
    subscribers.forEach((fn) => { try { fn(entry); } catch { /* swallow */ } });
}

export function onLog(fn: (e: LogEntry) => void): () => void {
    subscribers.add(fn);
    return () => { subscribers.delete(fn); };
}

function fmt(args: unknown[]): string {
    return args.map((a) => {
        if (a instanceof Error) return a.message;
        if (typeof a === 'string') return a;
        try { return JSON.stringify(a); } catch { return String(a); }
    }).join(' ');
}

let captureInstalled = false;
export function installConsoleCapture(): () => void {
    if (captureInstalled) return () => { /* no-op double install */ };
    captureInstalled = true;
    const origWarn = console.warn.bind(console);
    const origError = console.error.bind(console);
    console.warn = (...args: unknown[]): void => { try { log('warn', fmt(args)); } catch { /* swallow */ } origWarn(...args); };
    console.error = (...args: unknown[]): void => { try { log('error', fmt(args)); } catch { /* swallow */ } origError(...args); };
    return () => {
        console.warn = origWarn;
        console.error = origError;
        captureInstalled = false;
    };
}

// Curated intent taps. Quiet by design — we record decisions the user
// likely cares to see in retrospect (what dataset / theme / scene change
// happened), not every internal tick.
export function installIntentTaps(store: Store<State>): () => void {
    const offs: Array<() => void> = [];

    offs.push(store.onIntent(INTENTS.SWITCH_DATASET, (p) => log('info', `dataset → ${p.key}`)));
    offs.push(store.onIntent(INTENTS.SET_FIELD_COMPONENT, (p) => log('info', `field → ${p.component}`)));
    offs.push(store.onIntent(INTENTS.CYCLE_THEME, () => log('info', 'theme cycled')));
    offs.push(store.onIntent(INTENTS.LOAD_THEME, (p) => log('info', `theme ← ${p.url}`)));
    offs.push(store.onIntent(INTENTS.GENERATE_CONTOUR, () => log('info', 'contour generated')));
    offs.push(store.onIntent(INTENTS.CLEAR_CONTOURS, () => log('info', 'contours cleared')));
    offs.push(store.onIntent(INTENTS.TRACE_FIELDLINE, () => log('info', 'fieldline traced')));
    offs.push(store.onIntent(INTENTS.CLEAR_FIELDLINES, () => log('info', 'fieldlines cleared')));
    offs.push(store.onIntent(INTENTS.UI_SCREENSHOT_SAVE, () => log('info', 'screenshot saved')));
    offs.push(store.onIntent(INTENTS.UI_LOADING_SHOW, (p) => log('debug', `loading: ${(p && p.msg) || ''}`.trim())));
    offs.push(store.onIntent(INTENTS.UI_LOADING_HIDE, () => log('debug', 'loading done')));

    return () => { offs.forEach((off) => { try { off(); } catch { /* swallow */ } }); };
}
